import React from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {localizeTitle} from '@entities/job-templates/lib';
import {JobTemplatesGate, jobTemplatesModel} from '@entities/job-templates/model';
import {JobDictionaryItem, JobTemplateDto} from '@entities/job-templates/types';
import {Input} from '@shared/ui/Input';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';

import {ControlsCard, SearchBox, Wrapper} from './styled';

const formatDate = (value?: string | null) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    return format(date, 'dd.MM.yyyy');
};

const statusColor = (code?: string | null) =>
    code === 'ENABLED' ? STATUS_COLOR_LIST.GREEN : STATUS_COLOR_LIST.GRAY_ALT;

const getColumnsConfig = (
    t: (key: string, params?: Record<string, unknown>) => string,
    statusOptions: Array<SelectOption<string>>,
) => [
    {id: 'name', header: t('page_audit.terms.job-template'), minWidth: 496},
    {id: 'version', header: t('page_audit.terms.version'), width: 100},
    {id: 'title', header: t('page_audit.terms.job-type'), minWidth: 298},
    {
        id: 'status',
        header: t('page_audit.terms.status'),
        width: 200,
        filter: <Select isClearable options={statusOptions} />,
    },
];

export const JobTemplatesTab = () => {
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const model = useUnit(jobTemplatesModel);

    useGate(JobTemplatesGate);

    const statusOptions = React.useMemo(
        () =>
            model.state.activeStatuses.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, model.state.activeStatuses],
    );

    const data = React.useMemo(
        () =>
            (model.state.data?.data ?? []).map((row: JobTemplateDto) => {
                const status = model.state.activeStatuses.find(
                    (item: JobDictionaryItem) => item.id === row.status || item.code === row.status,
                );
                const title = localizeTitle(row.title, i18n.language);

                return {
                    ...row,
                    name: t('page_audit.terms.job-template-name-and-version-short', {
                        name: title,
                        version: formatDate(row.updated),
                    }),
                    version: row.version || EMPTY,
                    title,
                    status: (
                        <StatusWrapper color={statusColor(status?.code)}>
                            {localizeTitle(status?.title, i18n.language)}
                        </StatusWrapper>
                    ),
                };
            }),
        [i18n.language, model.state.activeStatuses, model.state.data, t],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                getColumnsConfig(t, statusOptions),
                model.state.filters,
                model.filtersChanged,
                model.filtersReset,
                model.state.sortings,
                model.sortingsChanged,
            ),
        [
            model.filtersChanged,
            model.filtersReset,
            model.sortingsChanged,
            model.state.filters,
            model.state.sortings,
            statusOptions,
            t,
        ],
    );

    return (
        <Wrapper>
            <ControlsCard>
                <SearchBox>
                    <Input
                        size="sm"
                        value={model.state.search}
                        placeholder={t('page_audit.page_jobs.search_placeholder')}
                        onChange={(event) => model.searchChanged(event.target.value)}
                    />
                </SearchBox>
            </ControlsCard>

            <Table
                columns={columns}
                data={data}
                isLoading={model.state.pending}
                pageCount={Math.ceil((model.state.data?.total ?? 0) / model.state.pageSize)}
                pageIndex={model.state.pageNumber}
                totalRowsCount={model.state.data?.total ?? 0}
                onPageChange={model.pageNumberChanged}
                onPageSizeChange={model.pageSizeChanged}
                onRowClick={(row) =>
                    history.push(
                        ROUTES.PREFERENCES_JOB_TEMPLATE_DETAILS.replace(
                            ':jobTemplateId',
                            row.original.id,
                        ),
                    )
                }
            />
        </Wrapper>
    );
};
